import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function DashboardShell({
  Sidebar,
  Topbar,
  sidebarProps = {},
  collapsed,
  setCollapsed,
  children,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches,
  );
  const location = useLocation();
  const shellRef = useRef(null);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!mobile || !mobileOpen) return;
    const oldOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    const focusables = () => [
      ...(shellRef.current?.querySelectorAll(
        "aside a[href], aside button:not(:disabled), .erp-mobile-nav-toggle",
      ) || []),
    ];
    focusables()[0]?.focus();
    const escape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
      if (event.key !== "Tab") return;
      const nodes = focusables();
      const first = nodes[0],
        last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", escape);
    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener("keydown", escape);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [mobile, mobileOpen]);
  return (
    <div
      ref={shellRef}
      className="erp-ui erp-shell"
      data-collapsed={collapsed}
      data-mobile-open={mobileOpen}
    >
      <a href="#dashboard-content" className="erp-skip-link">
        Skip to content
      </a>
      {mobileOpen && (
        <button
          type="button"
          className="erp-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <Sidebar
        {...sidebarProps}
        collapsed={mobile ? false : collapsed}
        setCollapsed={mobile ? () => setMobileOpen(false) : setCollapsed}
      />
      <Topbar collapsed={collapsed} />
      <button
        type="button"
        className="erp-mobile-nav-toggle"
        aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      <main id="dashboard-content" className="erp-content" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
